/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { MessageType } from "../enums/MessageType";
import type { IMessageService } from "../interfaces/IMessageService";
import { Log } from "../logging/Log";
import type { TMessageServiceRequest } from "../types/TMessageServiceRequest";
import type { TMessageServiceResponse } from "../types/TMessageServiceResponse";

/**
 * Augment globalThis to declare the Vega OS platform flag
 * and the ReactNativeWebView bridge injected by the Vega OS WebView.
 */
declare global {
  var __IS_VEGA_OS_PLATFORM__: boolean | undefined;

  interface Window {
    ReactNativeWebView?: {
      postMessage(message: string): void;
    };
  }
}

/**
 * MessageService facilitates request/response communication between
 * the WebApp (iframe / page) and the Shell App (parent window).
 *
 * It supports two transport modes:
 *  - **Vega OS**: Posts via `ReactNativeWebView.postMessage` (native bridge).
 *  - **Regular web**: Posts via `window.parent.postMessage`.
 *
 * The service registers a one-time `message` event listener, sends the
 * request payload, and returns a Promise that resolves when the Shell App
 * replies with a matching `messageServiceResponse`, or rejects on timeout.
 */
export class MessageService implements IMessageService {
  private static readonly TAG: string = "MessageService";

  /**
   * Default timeout in milliseconds before the service request is rejected.
   */
  private static readonly SERVICE_TIMEOUT_MS: number = 3000;

  /**
   * Name of the Shell App service to invoke.
   */
  private readonly mServiceName: string;

  /**
   * Arguments to pass to the Shell App service.
   */
  private readonly mServiceArgs: Record<string, unknown>;

  /**
   * @param serviceName - Name of the Shell App service to invoke
   * @param serviceArgs - Arguments to pass to the Shell App service (default: empty object)
   */
  public constructor(
    serviceName: string,
    serviceArgs: Record<string, unknown> = {}
  ) {
    this.mServiceName = serviceName;
    this.mServiceArgs = serviceArgs;
  }

  /**
   * Build the request payload to be posted to the Shell App.
   * @returns The structured message service request payload
   */
  private buildRequestPayload(): TMessageServiceRequest {
    return {
      type: MessageType.MESSAGE_SERVICE_REQUEST,
      serviceName: this.mServiceName,
      serviceArgs: this.mServiceArgs
    };
  }

  /**
   * Determine whether the current runtime environment is Vega OS.
   * @returns true if running inside the Vega OS ReactNativeWebView
   */
  private isVegaOsPlatform(): boolean {
    return globalThis.__IS_VEGA_OS_PLATFORM__ === true;
  }

  /**
   * Post the message payload to the Shell App using the appropriate
   * transport for the current platform.
   * @param payload - The message service request payload
   */
  private postMessageToShellApp(payload: TMessageServiceRequest): void {
    if (this.isVegaOsPlatform()) {
      Log.d(
        MessageService.TAG,
        `postMessageToShellApp: Sending via ReactNativeWebView for service "${this.mServiceName}"`
      );
      if (
        window.ReactNativeWebView !== null &&
        window.ReactNativeWebView !== undefined
      ) {
        window.ReactNativeWebView.postMessage(JSON.stringify(payload));
      } else {
        Log.e(
          MessageService.TAG,
          `postMessageToShellApp: ReactNativeWebView is not available on Vega OS platform`
        );
      }
    } else {
      Log.d(
        MessageService.TAG,
        `postMessageToShellApp: Sending via window.parent.postMessage for service "${this.mServiceName}"`
      );
      // NOSONAR: Wildcard origin is required because the Shell App (parent window) may be on a
      // different origin than the WebApp, especially on Smart TV platforms using file:// or custom schemes.
      window.parent.postMessage(payload, "*"); // NOSONAR
    }
  }

  /**
   * Send a service request to the Shell App and return a Promise
   * that resolves with the Shell App's response.
   *
   * Workflow:
   * 1. Registers a `message` event listener on the window.
   * 2. Posts the request payload to the Shell App (Vega OS or regular web).
   * 3. Starts a timeout timer (3000 ms).
   * 4. On receiving a matching `messageServiceResponse`, clears the timeout and resolves.
   * 5. If no response arrives within the timeout, logs an error and rejects.
   *
   * @returns Promise resolving to the Shell App service response data
   */
  public getResponse(): Promise<unknown> {
    if (window.self === window.top && !this.isVegaOsPlatform())
      return Promise.resolve(true);

    const payload: TMessageServiceRequest = this.buildRequestPayload();

    return new Promise<unknown>((resolve, reject) => {
      let serviceTimeoutId: ReturnType<typeof setTimeout> | null = null;

      const messageEventHandler = (event: MessageEvent): void => {
        const data = event.data as TMessageServiceResponse;

        if (data === null || data === undefined) {
          return;
        }

        if (
          data.type === MessageType.MESSAGE_SERVICE_RESPONSE &&
          data.serviceName === this.mServiceName
        ) {
          Log.d(
            MessageService.TAG,
            `getResponse: Received response for service "${this.mServiceName}"`
          );

          if (serviceTimeoutId !== null) {
            clearTimeout(serviceTimeoutId);
            serviceTimeoutId = null;
          }

          window.removeEventListener("message", messageEventHandler);
          resolve(data.response);
        }
      };

      window.addEventListener("message", messageEventHandler);

      this.postMessageToShellApp(payload);

      serviceTimeoutId = setTimeout(() => {
        Log.e(
          MessageService.TAG,
          `getResponse: Timeout waiting for response from service "${this.mServiceName}" after ${MessageService.SERVICE_TIMEOUT_MS}ms`
        );
        window.removeEventListener("message", messageEventHandler);
        reject(
          new Error(
            `MessageService timeout: No response from "${this.mServiceName}" within ${MessageService.SERVICE_TIMEOUT_MS}ms`
          )
        );
      }, MessageService.SERVICE_TIMEOUT_MS);
    });
  }

  /**
   * Get the service name for this message service instance.
   * @returns The service name string
   */
  public getServiceName(): string {
    return this.mServiceName;
  }

  /**
   * Get the service arguments for this message service instance.
   * @returns The service arguments object
   */
  public getServiceArgs(): Record<string, unknown> {
    return this.mServiceArgs;
  }

  /**
   * String representation for debugging.
   * @returns Debug string for this MessageService instance
   */
  public toString(): string {
    return `MessageService(serviceName=${this.mServiceName}, serviceArgs=${JSON.stringify(this.mServiceArgs)})`;
  }
}
